import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { Dialog, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GavelIcon from '@mui/icons-material/Gavel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReportOutlinedIcon from '@mui/icons-material/ReportOutlined';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import BusinessIcon from '@mui/icons-material/Business';
import { useHotkeys } from 'react-hotkeys-hook';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import { menuItems, urlRegex } from '../../utils';
import { refangDomain } from '../../utils/domainUtils';
import { parseDomainList } from '../../utils/domainLookup';
import Domain360Dialog from '../Domain360/Domain360Dialog';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import KeyboardShortcutsHelp from '../ThreatTable/KeyboardShortcutsHelp';
import { getShortcutsForRoute } from '../../hooks/keyboardShortcuts.constants';
import { useCompaniesQuery } from '../../queries/lookups';
import { showToast } from '../../store/toaster/action';
import { useAuth0 } from '../../__mocks__/@auth0/auth0-react';
import { PaletteCompany, PaletteNavItem } from './types';
import { PaletteFooter, PaletteRoot } from './Style/CommandPalette.style';
import { OPEN_COMMAND_PALETTE_EVENT } from './commandPaletteBus';
import {
  PaletteAction,
  capturePaletteAction,
  capturePaletteOpened,
} from './commandPaletteAnalytics';

export { openCommandPalette } from './commandPaletteBus';

const MAX_COMPANY_RESULTS = 8;

/**
 * Matches "td <id>" / "takedown <id>" / "td #<id>" → the takedown id.
 * Ids are UUIDs (e.g. "td 6822c598-3c57-4e6e-87a7-a1f8cb1299e5"); numeric
 * ids are accepted too.
 */
export const parseTakedownIdQuery = (query: string): string | null => {
  const match = query
    .trim()
    .match(
      /^(?:td|takedown)\s*#?\s*([0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}|\d+)$/i,
    );
  return match ? match[1] : null;
};

/** Flattens menuItems (incl. Settings submenus) into palette entries. */
export const buildNavItems = (
  flagValues: Record<string, boolean>,
): PaletteNavItem[] => {
  const items: PaletteNavItem[] = [];
  menuItems
    .filter(item => !item.flag || flagValues[item.flag])
    .forEach(item => {
      items.push({
        id: item.id,
        label: item.menuName,
        to: item.to,
        icon: item.icon,
      });
      item.submenu?.forEach(subitem => {
        items.push({
          id: subitem.id,
          label: `${item.menuName} — ${subitem.menuName}`,
          to: subitem.to,
          icon: subitem.icon,
        });
      });
    });
  return items;
};

const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [domain360Domain, setDomain360Domain] = useState<string | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth0();

  // Feature-flag gated menu items — keep in sync with Sidebar.tsx
  const threatExplainStats = useFeatureFlag('threat-explain-stats');
  const flagValues: Record<string, boolean> = {
    'threat-explain-stats': threatExplainStats,
  };

  // Companies are blacklisted from redux-persist; only start fetching once
  // the palette has been opened so "go to company" has data to match
  // against (shared TanStack Query cache - WHEEL-336).
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const { data: companiesData } = useCompaniesQuery(hasOpenedOnce);
  const companies: PaletteCompany[] = useMemo(
    () => companiesData || [],
    [companiesData],
  );

  // The hotkey callback is memoized with [] deps, so read the live open
  // state through a ref rather than the closure.
  const openRef = useRef(open);
  openRef.current = open;

  useHotkeys(
    'mod+k',
    () => {
      if (!openRef.current) {
        capturePaletteOpened('shortcut', window.location.pathname);
      }
      setOpen(prev => !prev);
    },
    { enableOnFormTags: ['input', 'textarea', 'select'], preventDefault: true },
    [],
  );

  useEffect(() => {
    const handleOpen = () => {
      capturePaletteOpened('header_button', window.location.pathname);
      setOpen(true);
    };
    window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, handleOpen);
    return () =>
      window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, handleOpen);
  }, []);

  useEffect(() => {
    if (open) setHasOpenedOnce(true);
  }, [open]);

  const navItems = useMemo(
    () => buildNavItems(flagValues),
    [threatExplainStats], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const trimmedQuery = query.trim();
  const takedownIdMatch = parseTakedownIdQuery(trimmedQuery);
  const isDomainQuery = trimmedQuery ? urlRegex.test(trimmedQuery) : false;
  // Defang-tolerant: "hxxps://evil[.]com" still counts as a domain query.
  const refangedQuery = trimmedQuery ? refangDomain(trimmedQuery) : '';
  const isDomain360Query = refangedQuery ? urlRegex.test(refangedQuery) : false;
  // A pasted list (2+ domains) deeplinks to the bulk Domain lookup page.
  const queryDomains = useMemo(
    () => parseDomainList(trimmedQuery).domains,
    [trimmedQuery],
  );
  const isDomainListQuery = queryDomains.length > 1;

  const companyMatches = useMemo(() => {
    const lowered = trimmedQuery.toLowerCase();
    if (lowered.length < 2) return [];
    return companies
      .filter(company => company?.Name?.toLowerCase().includes(lowered))
      .slice(0, MAX_COMPANY_RESULTS);
  }, [companies, trimmedQuery]);

  const closePalette = () => {
    setOpen(false);
    setQuery('');
  };

  // Every item select goes through here; esc/backdrop closes do not, so
  // opens without an action stay visible as opened - action in PostHog.
  const selectAction = (action: PaletteAction) => {
    capturePaletteAction(action, Boolean(trimmedQuery), location.pathname);
    closePalette();
  };

  const handleNavigate = (to: string, action: PaletteAction = 'nav') => {
    selectAction(action);
    navigate(to);
  };

  const handleThreatSearch = () => {
    const searchQuery = trimmedQuery;
    selectAction('threat_search');
    navigate(`/dashboard?threat_search=${encodeURIComponent(searchQuery)}`);
  };

  const handleTakedownSearch = () => {
    const searchQuery = trimmedQuery;
    selectAction('takedown_search');
    navigate(`/takedown?takedown_search=${encodeURIComponent(searchQuery)}`);
  };

  const handleOpenTakedown = (takedownId: string) => {
    selectAction('takedown_open');
    navigate(`/takedown/${takedownId}`);
  };

  // ?domains= (even empty) opens the False Positive load modal — see the
  // searchParams effect in FalsePositive.tsx.
  const handleReportFalsePositive = (domain?: string) => {
    selectAction('fp_report');
    navigate(
      `/false-positive?domains=${domain ? encodeURIComponent(domain) : ''}`,
    );
  };

  const handleCreateThreat = () => {
    selectAction('create_threat');
    navigate('/dashboard?create_threat=true');
  };

  const handleCopyUrl = async () => {
    selectAction('url_copy');
    try {
      await navigator.clipboard.writeText(window.location.href);
      dispatch(showToast('Page URL copied to clipboard', 'success'));
    } catch {
      dispatch(showToast('Could not copy URL to clipboard', 'error'));
    }
  };

  const handleOpenShortcutsHelp = () => {
    selectAction('shortcuts_help');
    setShowShortcutsHelp(true);
  };

  const handleDomain360 = () => {
    const domain = refangedQuery;
    selectAction('domain_360');
    setDomain360Domain(domain);
  };

  const handleDomainLookup = () => {
    const domains = queryDomains.join(',');
    selectAction('domain_lookup');
    navigate(`/domain-lookup?domains=${encodeURIComponent(domains)}`);
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={closePalette}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
        transitionDuration={0}
        sx={{
          zIndex: 1400,
          '& .MuiDialog-container': { alignItems: 'flex-start' },
          '& .MuiDialog-paper': { mt: '12vh' },
        }}
        data-testid="command-palette-dialog"
      >
        <PaletteRoot>
          <Command label="Command palette" loop>
            <Command.Input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder="Type a command, page, threat or company name..."
              data-testid="command-palette-input"
            />
            <Command.List>
              {/* No Command.Empty: with a query the force-mounted search
                  actions below always render, so "No results" would only
                  ever show as misleading noise above them. */}
              {takedownIdMatch && (
                <Command.Item
                  forceMount
                  value={`takedown-open ${trimmedQuery}`}
                  onSelect={() => handleOpenTakedown(takedownIdMatch)}
                  data-testid="command-palette-takedown-open"
                >
                  <OpenInNewIcon fontSize="small" />
                  <span>Open takedown #{takedownIdMatch}</span>
                </Command.Item>
              )}
              {isDomainListQuery && (
                <Command.Item
                  forceMount
                  value={`domain-lookup ${trimmedQuery}`}
                  onSelect={handleDomainLookup}
                  data-testid="command-palette-domain-lookup"
                >
                  <PlaylistPlayIcon fontSize="small" />
                  <span>Look up {queryDomains.length} domains</span>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 'auto', color: 'text.secondary' }}
                  >
                    exact match
                  </Typography>
                </Command.Item>
              )}
              {/* A pasted list can't be a single threat/takedown search. */}
              {trimmedQuery && !isDomainListQuery && (
                <>
                  <Command.Item
                    forceMount
                    value={`threat-search ${trimmedQuery}`}
                    onSelect={handleThreatSearch}
                    data-testid="command-palette-threat-search"
                  >
                    <SearchIcon fontSize="small" />
                    <span>Search threats for &ldquo;{trimmedQuery}&rdquo;</span>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 'auto', color: 'text.secondary' }}
                    >
                      bypass filters
                    </Typography>
                  </Command.Item>
                  <Command.Item
                    forceMount
                    value={`takedown-search ${trimmedQuery}`}
                    onSelect={handleTakedownSearch}
                    data-testid="command-palette-takedown-search"
                  >
                    <GavelIcon fontSize="small" />
                    <span>
                      Search takedowns for &ldquo;{trimmedQuery}&rdquo;
                    </span>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 'auto', color: 'text.secondary' }}
                    >
                      bypass filters
                    </Typography>
                  </Command.Item>
                  {isDomain360Query && (
                    <Command.Item
                      forceMount
                      value={`domain-360 ${trimmedQuery}`}
                      onSelect={handleDomain360}
                      data-testid="command-palette-domain-360"
                    >
                      <TravelExploreIcon fontSize="small" />
                      <span>Domain 360 for &ldquo;{refangedQuery}&rdquo;</span>
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 'auto', color: 'text.secondary' }}
                      >
                        all sources
                      </Typography>
                    </Command.Item>
                  )}
                  {isDomainQuery && (
                    <Command.Item
                      forceMount
                      value={`fp-report-domain ${trimmedQuery}`}
                      onSelect={() => handleReportFalsePositive(trimmedQuery)}
                      data-testid="command-palette-fp-report-domain"
                    >
                      <ReportOutlinedIcon fontSize="small" />
                      <span>
                        Report &ldquo;{trimmedQuery}&rdquo; as false positive
                      </span>
                    </Command.Item>
                  )}
                </>
              )}
              <Command.Group heading="Actions">
                <Command.Item
                  value="create threat new"
                  onSelect={handleCreateThreat}
                  data-testid="command-palette-threat-create"
                >
                  <AddCircleOutlineIcon fontSize="small" />
                  <span>Create threat</span>
                </Command.Item>
                <Command.Item
                  value="report false positive domain load"
                  onSelect={() => handleReportFalsePositive()}
                  data-testid="command-palette-fp-report"
                >
                  <ReportOutlinedIcon fontSize="small" />
                  <span>Report false positive</span>
                </Command.Item>
                <Command.Item
                  value="copy current page url link share"
                  onSelect={handleCopyUrl}
                  data-testid="command-palette-url-copy"
                >
                  <ContentCopyIcon fontSize="small" />
                  <span>Copy current page URL</span>
                </Command.Item>
                <Command.Item
                  value="keyboard shortcuts help"
                  onSelect={handleOpenShortcutsHelp}
                  data-testid="command-palette-shortcuts-open"
                >
                  <KeyboardIcon fontSize="small" />
                  <span>Keyboard shortcuts</span>
                </Command.Item>
              </Command.Group>
              <Command.Group heading="Go to page">
                {navItems.map(item => (
                  <Command.Item
                    key={item.id}
                    value={`page ${item.label}`}
                    onSelect={() => handleNavigate(item.to)}
                    data-testid={`command-palette-nav-${item.id}`}
                  >
                    {React.cloneElement(item.icon as React.ReactElement, {
                      fontSize: 'small',
                    })}
                    <span>{item.label}</span>
                  </Command.Item>
                ))}
              </Command.Group>
              {companyMatches.length > 0 && (
                <Command.Group heading="Companies">
                  {companyMatches.map(company => (
                    <Command.Item
                      key={company.Id}
                      value={`company ${company.Name} ${company.Id}`}
                      onSelect={() =>
                        handleNavigate(
                          `/companies/${company.Id}/view`,
                          'company',
                        )
                      }
                      data-testid={`command-palette-company-${company.Id}`}
                    >
                      <BusinessIcon fontSize="small" />
                      <span>{company.Name}</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
            </Command.List>
          </Command>
          <PaletteFooter>
            <Typography variant="caption" color="text.secondary">
              ↑↓ navigate · ↵ select · esc close
            </Typography>
          </PaletteFooter>
        </PaletteRoot>
      </Dialog>
      <KeyboardShortcutsHelp
        open={showShortcutsHelp}
        onClose={() => setShowShortcutsHelp(false)}
        shortcuts={getShortcutsForRoute(location.pathname)}
      />
      <Domain360Dialog
        domain={domain360Domain ?? ''}
        open={Boolean(domain360Domain)}
        onClose={() => setDomain360Domain(null)}
      />
    </>
  );
};

export default CommandPalette;
